/**
 * CSV Upload Modal Component
 *
 * Handles bulk CSV upload of phone numbers.
 */

import { useState, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import { cn } from '@/utils';
import { Logger, LogCategory, logDataOperation } from '@/utils/logger';
import { UploadError, ErrorCode } from '@/utils/errorHandler';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { usePhoneStore, useCallGroups } from '@/stores/phoneStore';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { VALIDATION_RULES } from '@/constants';
import type { CsvUploadResult, CsvRowError, PhoneNumber } from '@/types';
import { Upload, FileText, AlertTriangle, CheckCircle2, X, Download } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface CsvRow {
  phone_number?: string;
  phoneNumber?: string;
  number?: string;
  phone?: string;
  phone_number_group?: string;
  phoneNumberGroup?: string;
  group?: string;
  description?: string;
  notes?: string;
  internal_id?: string;
  internalId?: string;
  [key: string]: string | undefined;
}

interface CsvUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function CsvUploadModal({ isOpen, onClose }: CsvUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addPhones } = usePhoneStore();
  const callGroups = useCallGroups();
  const { user, company } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CsvUploadResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'complete'>('upload');

  const resetState = () => {
    setFile(null);
    setParseResult(null);
    setStep('upload');
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    Logger.info(LogCategory.UI, 'CSV upload modal closed', 'CsvUploadModal');
    resetState();
    onClose();
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
  };

  const normalizePhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    return phone;
  };

  const parseFile = useCallback((fileToparse: File) => {
    Logger.info(LogCategory.UPLOAD, 'Parsing CSV file', 'CsvUploadModal', {
      fileName: fileToparse.name,
      fileSize: fileToparse.size,
    });
    Logger.startTimer('csvParse');

    setIsProcessing(true);

    // Validate file size
    if (fileToparse.size > VALIDATION_RULES.CSV_MAX_FILE_SIZE) {
      Logger.error(LogCategory.UPLOAD, 'File too large', 'CsvUploadModal', {
        fileSize: fileToparse.size,
        maxSize: VALIDATION_RULES.CSV_MAX_FILE_SIZE,
      });
      showError('File too large', 'Maximum file size is 5MB.');
      setIsProcessing(false);
      return;
    }

    Papa.parse<CsvRow>(fileToparse, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        Logger.endTimer('csvParse');
        Logger.info(LogCategory.UPLOAD, 'CSV parsing complete', 'CsvUploadModal', {
          totalRows: results.data.length,
          parseErrors: results.errors.length,
        });

        const errors: CsvRowError[] = [];
        const validPhones: Partial<PhoneNumber>[] = [];

        // Check row limit
        if (results.data.length > VALIDATION_RULES.CSV_MAX_ROWS) {
          Logger.error(LogCategory.UPLOAD, 'Too many rows', 'CsvUploadModal', {
            rowCount: results.data.length,
            maxRows: VALIDATION_RULES.CSV_MAX_ROWS,
          });
          showError('Too many rows', `Maximum ${VALIDATION_RULES.CSV_MAX_ROWS.toLocaleString()} rows allowed.`);
          setIsProcessing(false);
          return;
        }

        results.data.forEach((row, index) => {
          const rowNum = index + 2; // Account for header row

          // Find phone number field
          const phoneValue =
            row.phone_number || row.phonenumber || row.number || row.phone || '';

          if (!phoneValue) {
            errors.push({
              row: rowNum,
              field: 'phone_number',
              value: '',
              message: 'Phone number is required',
            });
            return;
          }

          if (!validatePhoneNumber(phoneValue)) {
            errors.push({
              row: rowNum,
              field: 'phone_number',
              value: phoneValue,
              message: 'Invalid phone number format',
            });
            return;
          }

          // Validate group if provided
          const groupValue = row.phone_number_group || row.phonenumbergroup || row.group || '';
          const validGroupNames = callGroups.map(g => g.name.toLowerCase());

          if (groupValue && !validGroupNames.includes(groupValue.toLowerCase())) {
            errors.push({
              row: rowNum,
              field: 'group',
              value: groupValue,
              message: `Group "${groupValue}" does not exist. Available groups: ${callGroups.map(g => g.name).join(', ')}`,
            });
            return;
          }

          // Match to exact group name (case-insensitive lookup, preserve original casing)
          const matchedGroup = groupValue
            ? callGroups.find(g => g.name.toLowerCase() === groupValue.toLowerCase())
            : null;

          // Build phone object
          const phone: Partial<PhoneNumber> = {
            number: normalizePhoneNumber(phoneValue),
            callGroup: matchedGroup?.name || null,
            description: row.description || row.notes || null,
            internalId: row.internal_id || row.internalid || null,
          };

          validPhones.push(phone);
        });

        // Add parse errors
        results.errors.forEach((err) => {
          errors.push({
            row: err.row || 0,
            field: 'parse',
            value: '',
            message: err.message,
          });
        });

        const uploadResult: CsvUploadResult = {
          success: errors.length === 0,
          totalRows: results.data.length,
          validRows: validPhones.length,
          invalidRows: errors.length,
          errors: errors.slice(0, 100), // Limit displayed errors
          phones: validPhones,
        };

        Logger.info(LogCategory.UPLOAD, 'CSV validation complete', 'CsvUploadModal', {
          validRows: uploadResult.validRows,
          invalidRows: uploadResult.invalidRows,
        });

        logDataOperation('upload', 'csv', uploadResult.totalRows, {
          validRows: uploadResult.validRows,
          invalidRows: uploadResult.invalidRows,
        });

        setParseResult(uploadResult);
        setStep('preview');
        setIsProcessing(false);
      },
      error: (error) => {
        Logger.error(LogCategory.UPLOAD, 'CSV parsing failed', 'CsvUploadModal', undefined, error);
        showError('Parse error', 'Failed to parse CSV file. Please check the format.');
        setIsProcessing(false);
      },
    });
  }, [showError]);

  const handleFileSelect = (selectedFile: File) => {
    Logger.info(LogCategory.UI, 'File selected', 'CsvUploadModal', {
      fileName: selectedFile.name,
      fileType: selectedFile.type,
    });

    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.csv') && selectedFile.type !== 'text/csv') {
      Logger.warn(LogCategory.UPLOAD, 'Invalid file type', 'CsvUploadModal', {
        fileType: selectedFile.type,
      });
      showError('Invalid file type', 'Please upload a CSV file.');
      return;
    }

    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!parseResult || parseResult.phones.length === 0) {
      showError('No valid data', 'No valid phone numbers to upload.');
      return;
    }

    if (!file || !user || !company) {
      showError('Authentication error', 'Please log in to upload files.');
      return;
    }

    Logger.info(LogCategory.DATA, 'Starting phone upload', 'CsvUploadModal', {
      count: parseResult.phones.length,
    });
    setIsProcessing(true);

    try {
      // 1. Upload CSV file to Supabase Storage
      const fileName = `${company.id}/${Date.now()}_${file.name}`;
      const { data: storageData, error: storageError } = await supabase.storage
        .from('csv-uploads')
        .upload(fileName, file);

      if (storageError) {
        Logger.error(LogCategory.UPLOAD, 'File upload to storage failed', 'CsvUploadModal', undefined, storageError);
        throw storageError;
      }

      Logger.info(LogCategory.UPLOAD, 'File uploaded to storage', 'CsvUploadModal', {
        path: storageData.path,
      });

      // 2. Add phone numbers to database
      await addPhones(parseResult.phones);

      // 3. Record upload in csv_uploads table
      const { error: recordError } = await supabase
        .from('csv_uploads')
        .insert({
          company_id: company.id,
          uploaded_by_user_id: user.id,
          file_name: file.name,
          file_size: file.size,
          storage_path: storageData.path,
          total_rows: parseResult.totalRows,
          valid_rows: parseResult.validRows,
          invalid_rows: parseResult.invalidRows,
          errors: (parseResult.errors.length > 0 ? parseResult.errors : null) as any,
        } as any);

      if (recordError) {
        Logger.warn(LogCategory.UPLOAD, 'Failed to record upload', 'CsvUploadModal', undefined, recordError);
        // Non-critical, continue
      }

      Logger.info(LogCategory.DATA, 'Phone upload complete', 'CsvUploadModal');
      setStep('complete');
      showSuccess('Upload complete', `${parseResult.phones.length} phone numbers have been added.`);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Phone upload failed', 'CsvUploadModal', undefined, error);
      showError('Upload failed', 'Failed to add phone numbers. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    Logger.info(LogCategory.UI, 'Downloading CSV template', 'CsvUploadModal');
    const template = 'phone_number,phone_number_group,description,internal_id\n+15551234567,Pool 1,Example description,INT-001\n+15559876543,Pool 2,Another description,INT-002';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'phone_numbers_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Phone Numbers"
      description="Import phone numbers from a CSV file"
      size="lg"
    >
      {step === 'upload' && (
        <div className="space-y-6">
          {/* Drop zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={cn(
              'relative border-2 border-dashed rounded-xl p-8 transition-colors',
              dragActive ? 'border-primary-500 bg-primary-50' : 'border-slate-300 hover:border-slate-400'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <p className="text-lg font-medium text-slate-900">
                {dragActive ? 'Drop your file here' : 'Drag and drop your CSV file'}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                or click to browse
              </p>
              <p className="mt-4 text-xs text-slate-400">
                Maximum {VALIDATION_RULES.CSV_MAX_ROWS.toLocaleString()} rows, 5MB file size
              </p>
            </div>
          </div>

          {/* Template download */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-900">Need a template?</p>
                <p className="text-xs text-slate-500">Download our CSV template to get started</p>
              </div>
            </div>
            <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={downloadTemplate}>
              Download
            </Button>
          </div>

          {/* Format info */}
          <div className="text-sm text-slate-600">
            <p className="font-medium mb-2">Required columns:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li><code className="bg-slate-100 px-1 rounded">phone_number</code> - Phone number (10 or 11 digits)</li>
            </ul>
            <p className="font-medium mt-4 mb-2">Optional columns:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li><code className="bg-slate-100 px-1 rounded">phone_number_group</code> - Phone number group name</li>
              <li><code className="bg-slate-100 px-1 rounded">description</code> - Description or notes</li>
              <li><code className="bg-slate-100 px-1 rounded">internal_id</code> - Your internal ID</li>
            </ul>
          </div>
        </div>
      )}

      {step === 'preview' && parseResult && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900">{parseResult.totalRows}</p>
              <p className="text-sm text-slate-500">Total rows</p>
            </div>
            <div className="p-4 bg-success-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-success-600">{parseResult.validRows}</p>
              <p className="text-sm text-success-700">Valid</p>
            </div>
            <div className={cn(
              'p-4 rounded-lg text-center',
              parseResult.invalidRows > 0 ? 'bg-danger-50' : 'bg-slate-50'
            )}>
              <p className={cn(
                'text-2xl font-bold',
                parseResult.invalidRows > 0 ? 'text-danger-600' : 'text-slate-400'
              )}>
                {parseResult.invalidRows}
              </p>
              <p className={cn(
                'text-sm',
                parseResult.invalidRows > 0 ? 'text-danger-700' : 'text-slate-500'
              )}>
                Invalid
              </p>
            </div>
          </div>

          {/* Errors */}
          {parseResult.errors.length > 0 && (
            <div className="border border-danger-200 rounded-lg overflow-hidden">
              <div className="bg-danger-50 px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-danger-600" />
                <span className="text-sm font-medium text-danger-700">
                  {parseResult.errors.length} error{parseResult.errors.length !== 1 ? 's' : ''} found
                </span>
              </div>
              <div className="max-h-40 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Row</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Field</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {parseResult.errors.slice(0, 10).map((error, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-slate-900">{error.row}</td>
                        <td className="px-4 py-2 text-slate-600">{error.field}</td>
                        <td className="px-4 py-2 text-danger-600">{error.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parseResult.errors.length > 10 && (
                  <p className="px-4 py-2 text-sm text-slate-500">
                    ...and {parseResult.errors.length - 10} more errors
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={resetState}>
              Upload different file
            </Button>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                isLoading={isProcessing}
                disabled={parseResult.validRows === 0}
              >
                Upload {parseResult.validRows} number{parseResult.validRows !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'complete' && (
        <div className="text-center py-8">
          <div className="mx-auto h-16 w-16 bg-success-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="h-8 w-8 text-success-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Upload Complete!</h3>
          <p className="mt-2 text-slate-600">
            {parseResult?.validRows} phone numbers have been added to your account.
          </p>
          <Button onClick={handleClose} className="mt-6">
            Done
          </Button>
        </div>
      )}

      {isProcessing && step === 'upload' && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-sm text-slate-600">Processing file...</p>
          </div>
        </div>
      )}
    </Modal>
  );
}
